import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AnimatePresence, motion } from "framer-motion";
import PropTypes from "prop-types";
import { forwardRef } from "react";
import Skeleton from "react-loading-skeleton";

import { Button } from "@transfr-inc/dashboard-components/forms";

import { careerType } from "@features/career-details/types";
import { useCareerExpert } from "@features/career-expert/api/use-career-expert";
import { Tag } from "@features/ui";
import { ExpertIcon } from "@features/ui/expert-icon";
import { TextToolTip } from "@features/ui/tooltip";
import { OpacityTransitions } from "@lib/animation.helpers";
import {
  aOrAn,
  compactCurrencyFormat,
  currencyFormat,
} from "@lib/text.helpers";

import {
  FEDERAL_INCOME_TAX_TOOLTIP,
  FederalTaxBracketTooltip,
  IncomeTaxRatesDropdown,
  PercentageInfo,
} from "../components";

import {
  getFederalTaxBracket,
  SOCIAL_AND_MEDICARE_PERCENTAGE,
} from "../budget.helpers";

import "./take-home-pay-calculator.scss";

export const TakeHomePayCalculator = forwardRef(
  (
    {
      budgetPlan,
      career,
      isLoading,
      isTakeHomePayCalculated,
      onCalculateTakeHomePay,
      onRegionalIncomeTaxRateChange,
      state,
    },
    ref,
  ) => {
    const regionAltCode = state?.code;

    const { data: careerExpert } = useCareerExpert(career?.transfrId);
    const expertTitle = aOrAn(career?.title?.toLowerCase());

    const {
      annualIncome = 0,
      incomeTaxRate = {},
      taxInfo: {
        combinedTaxRate = 0,
        totalTaxDeduction = 0,
        netIncome = 0,
      } = {},
    } = budgetPlan;

    const federalIncomeTax = getFederalTaxBracket(annualIncome);

    return (
      <div ref={ref} className="take-home-pay-calculator">
        <div className="expert-info">
          <ExpertIcon transfrId={career?.transfrId} />
          <div className="expert-info-income">
            <div>
              {careerExpert?.name} is {expertTitle} that makes a{" "}
              <TextToolTip
                value="gross income"
                content="This is the total amount of money that you earn before taxes are subtracted."
              />{" "}
              of
            </div>
            <div className="expert-info-income-number">
              {isLoading ? (
                <Skeleton width="4rem" height="2.18rem" />
              ) : (
                <>
                  {currencyFormat(annualIncome)} <span> / year</span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="tax-info">
          <div>
            <TextToolTip
              value="Tax Bracket"
              content={<FederalTaxBracketTooltip />}
            />
            : {federalIncomeTax.shortLabel}
          </div>
          <div className="tax-info-detail">
            {isLoading ? (
              <>
                <Skeleton width="4.125rem" height="4.625rem" />+
                <Skeleton width="4.125rem" height="4.625rem" />+
                <Skeleton width="4.125rem" height="4.625rem" />=
                <Skeleton width="4.125rem" height="4.625rem" />
              </>
            ) : (
              <>
                <PercentageInfo
                  value={federalIncomeTax.percentage}
                  description="Federal"
                  tooltip={FEDERAL_INCOME_TAX_TOOLTIP}
                />
                <span>+</span>
                <PercentageInfo value={incomeTaxRate.averageTaxRate}>
                  <IncomeTaxRatesDropdown
                    value={incomeTaxRate}
                    onChange={onRegionalIncomeTaxRateChange}
                    regionAltCode={regionAltCode?.padStart(2, "0")}
                  />
                </PercentageInfo>
                <span>+</span>
                <PercentageInfo
                  value={SOCIAL_AND_MEDICARE_PERCENTAGE}
                  description="Social + Medicare"
                  tooltip="Social and medicare"
                />
                <span>=</span>
                <PercentageInfo
                  value={combinedTaxRate}
                  description={
                    combinedTaxRate ? (
                      `or ${compactCurrencyFormat(totalTaxDeduction)}`
                    ) : (
                      <FontAwesomeIcon icon={["fa-regular", "minus"]} />
                    )
                  }
                />
              </>
            )}
          </div>
        </div>
        <div className="take-home-info">
          <div>
            {isLoading ? (
              <Skeleton width="15rem" height="1.275rem" />
            ) : (
              <>
                Your{" "}
                <TextToolTip
                  value="net income"
                  content="This is the total amount of money that you have left, after taxes are subtracted from your Gross Income amount."
                />{" "}
                would be {compactCurrencyFormat(annualIncome)} -{" "}
                {compactCurrencyFormat(totalTaxDeduction ?? annualIncome)}
              </>
            )}
          </div>
          <div className="take-home-info-calculation">
            <AnimatePresence mode="popLayout">
              {isTakeHomePayCalculated ? (
                <motion.div
                  {...OpacityTransitions}
                  className="take-home-info-result"
                >
                  <div className="take-home-info-result-amount">
                    <span>
                      {isLoading ? (
                        <Skeleton width="4rem" height="2.18rem" />
                      ) : (
                        compactCurrencyFormat(netIncome)
                      )}
                    </span>
                    <Tag value="PER YEAR" />
                  </div>
                  <span>or</span>
                  <div className="take-home-info-result-amount">
                    <span>
                      {isLoading ? (
                        <Skeleton width="4rem" height="2.18rem" />
                      ) : (
                        currencyFormat(netIncome / 12)
                      )}
                    </span>
                    <Tag value="PER MONTH" />
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  className="take-home-info-button"
                  {...OpacityTransitions}
                >
                  <Button
                    disabled={!incomeTaxRate || isLoading}
                    onClick={onCalculateTakeHomePay}
                    icon={["fal", "calculator"]}
                  >
                    Calculate
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    );
  },
);

TakeHomePayCalculator.displayName = "Take Home Pay Calculator";

TakeHomePayCalculator.propTypes = {
  budgetPlan: PropTypes.object,
  career: careerType,
  isLoading: PropTypes.bool,
  isTakeHomePayCalculated: PropTypes.bool,
  onCalculateTakeHomePay: PropTypes.func,
  onRegionalIncomeTaxRateChange: PropTypes.func,
  regionalIncomeTaxRate: PropTypes.shape({
    id: PropTypes.number,
    averageTaxRate: PropTypes.number,
    regionAltCode: PropTypes.string,
  }),
  state: PropTypes.shape({
    code: PropTypes.string,
  }),
};
